import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Copy,
  Search,
  Filter,
  RefreshCw,
  GitBranch,
  CheckCircle,
  XCircle,
  Eye
} from 'lucide-react';
import { User } from '../../types';
import { Workflow } from '../../types/workflow';

interface WorkflowListProps {
  currentUser: User;
  onCreateWorkflow: () => void;
  onEditWorkflow: (workflowId: string) => void;
  onViewWorkflow: (workflowId: string) => void;
}

const WorkflowList: React.FC<WorkflowListProps> = ({
  currentUser,
  onCreateWorkflow,
  onEditWorkflow,
  onViewWorkflow
}) => {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);

      const response = await fetch('/api/workflows');
      if (response.ok) {
        const data = await response.json();
        setWorkflows(data);
      }
    } catch (err) {
      console.error('Error fetching workflows:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleToggleActive = async (workflowId: string, currentStatus: boolean) => {
    try {
      const response = await fetch(`/api/workflows/${workflowId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          isActive: !currentStatus,
          updatedBy: currentUser.id
        })
      });

      if (response.ok) {
        fetchWorkflows();
      }
    } catch (err) {
      console.error('Error toggling workflow status:', err);
    }
  };

  const handleDeleteWorkflow = async (workflowId: string, workflowName: string) => {
    if (!confirm(`Are you sure you want to delete workflow "${workflowName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/workflows/${workflowId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchWorkflows();
      }
    } catch (err) {
      console.error('Error deleting workflow:', err);
    }
  };

  const handleCloneWorkflow = async (workflowId: string, workflowName: string) => {
    const newName = prompt(`Enter name for cloned workflow:`, `${workflowName} (Copy)`);
    if (!newName) return;

    try {
      const response = await fetch(`/api/workflows/${workflowId}/clone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newName,
          createdBy: {
            id: currentUser.id,
            name: currentUser.name,
            email: currentUser.email
          }
        })
      });

      if (response.ok) {
        fetchWorkflows();
      }
    } catch (err) {
      console.error('Error cloning workflow:', err);
    }
  };

  const filteredWorkflows = workflows.filter(workflow => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!workflow.name.toLowerCase().includes(query) &&
          !workflow.description?.toLowerCase().includes(query)) {
        return false;
      }
    }

    // Active status filter
    if (filterActive === 'active' && !workflow.isActive) return false;
    if (filterActive === 'inactive' && workflow.isActive) return false;

    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Workflow Management</h1>
            <p className="text-sm text-gray-600 mt-1">
              Create and manage custom workflows for task automation
            </p>
          </div>
          <button
            onClick={onCreateWorkflow}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Workflow
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search workflows..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-600" />
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Workflows</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          <button
            onClick={() => fetchWorkflows(true)}
            disabled={refreshing}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Workflows Grid */}
      {filteredWorkflows.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <GitBranch className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No workflows found</h3>
          <p className="text-gray-600 mb-4">
            {searchQuery ? 'Try a different search term' : 'Get started by creating your first workflow'}
          </p>
          {!searchQuery && (
            <button
              onClick={onCreateWorkflow}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Workflow
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWorkflows.map((workflow) => (
            <div
              key={workflow._id}
              className="bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-shadow"
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{workflow.name}</h3>
                      {workflow.isDefault && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {workflow.description || 'No description'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggleActive(workflow.workflowId, workflow.isActive)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      workflow.isActive
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    title={workflow.isActive ? 'Active' : 'Inactive'}
                  >
                    {workflow.isActive ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <XCircle className="w-4 h-4" />
                    )}
                  </button>
                </div>

                {/* Metadata */}
                <div className="flex items-center gap-3 text-xs text-gray-600">
                  <span>v{workflow.version}</span>
                  <span>•</span>
                  <span>{workflow.states.length} states</span>
                  <span>•</span>
                  <span>{workflow.transitions.length} transitions</span>
                </div>
              </div>

              {/* Task Types */}
              <div className="p-4 border-b border-gray-200">
                <div className="text-xs font-medium text-gray-700 mb-2">Applies to:</div>
                <div className="flex flex-wrap gap-1">
                  {workflow.applicableTaskTypes.map((type) => (
                    <span
                      key={type}
                      className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded"
                    >
                      {type === 'all' ? 'All Task Types' : type.replace(/-/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>

              {/* Automation Rules */}
              {workflow.automationRules && workflow.automationRules.length > 0 && (
                <div className="p-4 border-b border-gray-200">
                  <div className="text-xs font-medium text-gray-700 mb-2">
                    Automation Rules: {workflow.automationRules.filter(r => r.enabled).length} active
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {workflow.automationRules.slice(0, 3).map((rule) => (
                      <span
                        key={rule.id}
                        className={`px-2 py-1 text-xs rounded ${
                          rule.enabled
                            ? 'bg-green-50 text-green-700'
                            : 'bg-gray-50 text-gray-600'
                        }`}
                      >
                        {rule.name}
                      </span>
                    ))}
                    {workflow.automationRules.length > 3 && (
                      <span className="px-2 py-1 text-xs bg-gray-50 text-gray-600 rounded">
                        +{workflow.automationRules.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="p-4 flex items-center justify-between">
                <div className="text-xs text-gray-600">
                  Created {new Date(workflow.createdAt).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onViewWorkflow(workflow.workflowId)}
                    className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="View"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onEditWorkflow(workflow.workflowId)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleCloneWorkflow(workflow.workflowId, workflow.name)}
                    className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                    title="Clone"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteWorkflow(workflow.workflowId, workflow.name)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkflowList;
